/**
 * All functionality needed in SpecSheet related components.
 *
 * @module feature/SpecSheet
 */
import Repository from 'FilterRepository';
import Filter from '../../../../../Foundation/Tags/code/src/scripts/tags/ImportsFilter.js';
import Pagination from '../../../../../Foundation/Pagination/code/src/scripts/pagination/Pagination.js';
import ResponsiveBackground from 'ResponsiveBackground';

/**
 * Filter and pagination functionality.
 *
 * @requires module:project/Common.FilterRepository
 * @requires module:project/Common.ResponsiveBackground
 * @requires module:foundation/Tags.ImportsFilter
 * @requires module:foundation/Pagination.Pagination
 * @memberof module:feature/SpecSheet
 * @version 1.0.0
 * @author Rocco Janse <rocco.janse@valtech.nl>
 */
class SpecSheetSearch {
    /**
     * Upgrades DOM element, adds filter, pagination and repository functionality.
     * @param {DOMElement} element DOM element to upgrade.
     */
    constructor(element) {
        this.$element = $(element);
        this.lang = this.$element.attr('data-lang');
        this.market = this.$element.attr('data-market') || false;
        this.country = this.$element.attr('data-country') || false;
        this.vehicleSerie = this.$element.attr('data-vehicle-serie');
        this.project = this.$element.attr('data-project');
        this.maxResults = this.$element.attr('data-items-per-page');
        this.$downloadAllSpecsheets = this.$element.find('.js-download-all-specsheets');

        let data = {};
        this.selectedData = {};

        if (this.market) {
            data.Market = this.market;
        }

        if (this.country) {
            data.Country = this.country;
        }

        if (this.vehicleSerie) {
            data.VehicleSerie = this.vehicleSerie;
        }

        if (this.project) {
            data.Project = this.project;
        }

        if (this.maxResults) {
            data.PageSize = this.maxResults;
        }

        this.repository = new Repository({
            apiBase: 'api/feature/specsheet/search',
            apiDownloadAll: 'api/feature/specsheet/download-all',
            lang: this.lang,
            data: data,
        });

        this.filter = new Filter(this.$element.find('.js-tag-filter'), this.repository).init();
        this.pagination = new Pagination(this.$element.find('.js-pagination'), this.repository, this.maxResults).init();

        this.$loader = this.$element.find('.loader').hide();

        this.$itemList = this.$element.find('.itemlist .itemlist__container');
        this.itemTemplate = null;

        this.baseUrl = window.location.hostname;

        this.repository.init();
    }

    /**
     * Initializes item row template and add listeners to repository.
     */
    init() {
        // get item template
        this.itemTemplate = this.buildTemplate(this.$itemList.find('.itemlist__item').first().clone());

        // repository listeners
        this.repository.addListener('beforeInit', () => {
            this.$loader.fadeIn(200);
        });
        this.repository.addListener('beforeUpdate', () => {
            this.$loader.fadeIn(200);
        });
        this.repository.addListener('updated', (pagedata) => {
            this.updateList(pagedata);
        });

        var repository = this.repository;

        this.$downloadAllSpecsheets.on('click', function (e) {
            repository.downloadAll(e);
        });
    }

    /**
     * Updates list with new items.
     * @param {object} data Json object or resultset to render in list.
     */
    updateList(data) {
        this.$loader.fadeIn(200, () => {
            if (data.Success === false) {
                this.$itemList.html(`<li class="row alert alert-danger" role="alert">${data.Reason}</div>`);

                // hide loader
                this.$loader.fadeOut(200);
            } else {
                let htmlArray = [];
                let promises = [];

                // render all items
                for (let item of data.Items) {
                    let promise = new Promise((resolve) => {
                        this.renderItem(item).then((renderedItem) => {
                            htmlArray.push(renderedItem);
                            resolve();
                        });
                    });
                    promises.push(promise);
                }

                // update list when all items rendered
                Promise.all(promises).then(() => {
                    // update DOM
                    this.$itemList.html(htmlArray.join(''));

                    // responsive backgrounds
                    this.$itemList.find('.js-responsive-background').each((index, el) => {
                        new ResponsiveBackground($(el)).init();
                    });

                    // update valtagmanager
                    if (window.digitalData) {
                        window.digitalData.update();
                    }

                    // hide loader
                    this.$loader.fadeOut(200);
                });
            }
        });
    }

    /**
     * Finds all variable elements and replaces it with tags
     * so we don't need find queries every time we render a card.
     * @param {jQueryDomElement}  $el Card to create template of.
     * @returns {string} HTML string of card.
     */
    buildTemplate($el) {
        const $wrapper = $('<div></div>').append($el);
        $wrapper.find('.img-fluid').attr('data-src', '%%IMAGEURL%%'); // work around for IE11
        $wrapper.find('.img-fluid').removeAttr('src');
        $wrapper.find('.item__description').html('%%DESCRIPTION%%');
        $wrapper.find('.item__spec-date').html('%%SPECDATE%%');
        $wrapper.find('.item__open-link--pdf').attr('href', '%%OPENPDFFILENAME%%');
        $wrapper.find('.item__download-link--zip').attr('href', '%%DOWNLOADPDFFILENAME%%');
        const html = $wrapper.html();
        return html;
    }

    /**
     * Renders item to template.
     * @param {object} item Json object of item to render.
     */
    renderItem(item) {
        return new Promise((resolve) => {
            let html = this.itemTemplate;
            html = html.replace(/%%IMAGEURL%%/g, `${item.AxleConfigurationImage}` || '');
            html = html.replace(/data-src/g, 'src'); // work around for IE11
            html = html.replace(/%%DESCRIPTION%%/g, item.Description || '');
            html = html.replace(/%%SPECDATE%%/g, item.SpecDate || '');
            if (item.PdfFilename !== '') {
                html = html.replace(
                    /%%OPENPDFFILENAME%%/g,
                    `https://${this.baseUrl}/api/feature/specsheet/open?filename=${item.PdfFilename}` ||
                        ''
                );
                html = html.replace(
                    /%%DOWNLOADPDFFILENAME%%/g,
                    `https://${this.baseUrl}/api/feature/specsheet/download?filename=${item.PdfFilename}` ||
                        ''
                );
                html = html.replace(/%%SHOWPDF%%/g, '');
            } else {
                html = html.replace(/%%SHOWPDF%%/g, 'd-none');
            }
            resolve(html);
        });
    }
}

// register to Component Handler
window.ComponentHandler.register({
    constructor: SpecSheetSearch,
    classAsString: 'SpecSheetSearch',
    cssClass: 'js-specsheet-search',
});

export default SpecSheetSearch;
